import * as React from "react";
import {ReactElement, useEffect} from "react";
import {useStore, useStoreValue, useSetStoreValue} from "react-context-hook";
import Icon from "../Icon";
import {getText} from "../../services/configuration.service";
import {getDateLabel, parseDate} from "../../services/label.service";

/**
 * MainLayout
 * @constructor
 */
const BookingCalendar = (): ReactElement => {
    const bookingEngineData = useStoreValue("bookingEngineData");
    const basePriceAvailability = ((bookingEngineData || {}).product || {}).basePriceAvailability;
    const enableCapacity = bookingEngineData?.product?.location;
    const selectedDepartureCity = useStoreValue("selectedDepartureCity");
    const availabilities = (bookingEngineData || {}).availabilities;
    const [selectedDepartureDate, setSelectedDepartureDate] = useStore("selectedDepartureDate");
    const selectedDuration = useStoreValue("selectedDuration");
    const selectedCapacity = useStoreValue("selectedCapacity");
    const setChooseCalendarDefaultDate = useSetStoreValue("calendarDefaultValue");

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useEffect(() => {
    }, [bookingEngineData, selectedDepartureCity, selectedDuration, selectedDepartureDate]);

    const selectDepartureDate = (departureDate: string) => {
        if (selectedDepartureDate.code !== departureDate) {
            const [selectedMonthCode, selectedDayCode] = parseDate(departureDate);
            setSelectedDepartureDate({code: departureDate,
                value: enableCapacity? availabilities[selectedDepartureCity.code][selectedCapacity.code][selectedDuration.code][selectedMonthCode][selectedDayCode]
                    :availabilities[selectedDepartureCity.code][selectedDuration.code][selectedMonthCode][selectedDayCode]});
        }
    };

    return (
        <div className="cpt-booking-calendar">
            <div className="booking-calendar-content">
                <div className="booking-calendar-month">
                    {!selectedDuration &&
                    <div className="placeholder-style">{getText("booking.loading.label")}</div>
                    }
                    {selectedDuration && selectedDuration.value &&
                    Object.keys(selectedDuration.value).sort((a: string , b: string) => sortDateByMonthYear(a, b)).map((monthCode: string) => {
                        const days = selectedDuration.value[monthCode];
                        if (days) {
                            const firstDay = days[Object.keys(days)[0]];
                            const priceTypeLabel = firstDay ?
                                (firstDay.byHousing ? getText("product.price.type.housing") : getText("product.price.type.person"))
                                : getText("booking.loading.label");
                            return (
                                <React.Fragment key={monthCode}>
                                    <div className="month-title">
                                        {getDateLabel("01-" + monthCode, "MMMM yyyy")}
                                        <div className="by-price">{getText("booking.price.label")} {getText("product.price.ttc")}/{priceTypeLabel}*</div>
                                    </div>
                                    {Object.keys(days).sort().map((dayNumber: string) => {
                                        const day = days[dayNumber];
                                        const isSelected = selectedDepartureDate.code === day.departureValue;
                                        const selectedCityIsBaseAvail = ((basePriceAvailability || {}).departureCity || {}).code === (selectedDepartureCity || {}).code;
                                        const selectedDurationIsBaseAvail = ((basePriceAvailability || {}).nbDays
                                            + "-" + (basePriceAvailability || {}).nbNights)
                                            === (selectedDuration || {}).code;
                                        const selectedDateIsBaseAvail = (basePriceAvailability || {}).departureDate === day.departureValue;
                                        const isBasePrice = selectedCityIsBaseAvail && selectedDurationIsBaseAvail && selectedDateIsBaseAvail;
                                        return (
                                            <div
                                                key={dayNumber + monthCode}
                                                onClick={() => {
                                                    setChooseCalendarDefaultDate(false);
                                                    selectDepartureDate(day.departureValue);
                                                }}
                                                className={`booking-calendar-item ${isSelected ? "selected" : ""}`}
                                            >
                                                <div className="bc-days-content">
                                                    <div className="bc-departure-day">
                                                        <div className="day">{getDateLabel(day.departureValue, "EEE")}</div>
                                                        <div className="number">{getDateLabel(day.departureValue, "dd")}</div>
                                                        <div className="month">{getDateLabel(day.departureValue, "MMM")}</div>
                                                    </div>
                                                    <div className="icon-wrap arrow-day">
                                                        <Icon icon="A"/>
                                                    </div>
                                                    <div className="bc-return-day">
                                                        <div className="day">{getDateLabel(day.returnValue, "EEE")}</div>
                                                        <div className="number">{getDateLabel(day.returnValue, "dd")}</div>
                                                        <div className="month">{getDateLabel(day.returnValue, "MMM")}</div>
                                                    </div>
                                                </div>
                                                <div className="bc-price-content">
                                                    {/*
                                                            It is possible to add another flag here :
                                                            <div className="discount-price">Remise de <strong>15%</strong></div>
                                                            <div className="bc-price">
                                                                <div className="old-price">350<span className="devise">&euro;</span></div>
                                                                <div className="real-price">327<span className="devise">&euro;</span></div>
                                                            </div>
                                                        */}
                                                    {isBasePrice && <div className="best-price">{getText("product.price.best.label")}</div>}
                                                    {day.reductionRate && <div className="discount-price">Remise de <strong>{day.reductionRate}%</strong></div>}
                                                    <div className="bc-price">
                                                        {day.regularPrice &&
                                                        <div className="old-price">{day.regularPrice}<span className="devise">{getText("product.price.currency")}</span></div>}
                                                        <div className="real-price">{day.price}<span className="devise">{getText("product.price.currency")}</span></div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        }
                    })
                    }
                </div>
            </div>
        </div>
    );
};
/**
 * Sort Date By Month & year
 */
function sortDateByMonthYear(a: string, b: string) {
    const date1 = a.split("-");
    const date2 = b.split("-");
    if (date1.length === 2 && date2.length === 2) {
        if ((date1[1] === date2[1] && date1[0] > date2[0]) || date1[1] > date2[1]) {
            return 1;
        }
    }
    return -1;
}

export default BookingCalendar;
